import { Injectable, OnDestroy } from "@angular/core";
import { UserService } from "./user.service";
import { EMPTY, Subject } from "rxjs";
import { takeUntil, catchError, finalize, tap } from "rxjs/operators";
import { AuthService } from "../../../auth/auth.service";
import { UserCache } from "./user.cache";

/** Provides methods to work with user data. */
export interface IUserManager {
	/**
	 * Refreshes the repair facilities for the user.
	 * @see RepairFacility
	 * @see UserService.getRepairFacilities
	 */
	refreshRepairFacilities(): void;
	/**
	 * Refreshes the contacts for the user.
	 * @see UserContact
	 * @see UserService.getContacts
	 */
	refreshContacts(): void;
}

@Injectable({
	providedIn: 'root'
})
export class UserManager implements IUserManager, OnDestroy {
	/**
	 * A subject that can be used to automatically end subscriptions when the component is destroyed.
	 * @private
	 */
	private readonly _destroying$ = new Subject<void>();

	// Updating flags.
	public updatingRepairFacilities: boolean;
	public updatingContacts: boolean;
	public readonly dataError$ = new Subject<void>();

	constructor(private userService: UserService,
	            private userCache: UserCache,
	            private authService: AuthService) {
		this.init();
	}

	// Initializes the user manager.
	init(): void {
		this.authService.loginStatusChanged
			.pipe(takeUntil(this._destroying$))
			// Refresh all user data when the login status changes.
			.subscribe(() => this.refreshAll());
		// If the component starts with the user logged in, perform a refresh.
		if (this.authService.isLoggedIn()) this.refreshAll();
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	// Refreshes all user data.
	public refreshAll(): void {
		// If not logged in, don't perform a refresh.
		if (!this.authService.isLoggedIn()) return;
		//TODO: If not logged in, wait until a login is complete.
		
		this.refreshRepairFacilities();
		this.refreshContacts();
	}

	public refreshRepairFacilities(): void {
		// If not logged in, don't perform a refresh.
		if (!this.authService.isLoggedIn()) return;
		//TODO: If not logged in, wait until a login is complete.
		
		this.updatingRepairFacilities = true;
		this.userService.getRepairFacilities()
			.pipe(
				takeUntil(this._destroying$),
				catchError(err => {
					this.dataError$.next();
					return EMPTY;
				}),
				tap(result => {
					if(!result || result.length === 0) {
						this.dataError$.next();
					}
				}),
				finalize(() => this.updatingRepairFacilities = false)
			)
			.subscribe(value => {
				this.userCache.setRepairFacilities(value);
			});
	}

	public refreshContacts(): void {
		// If not logged in, don't perform a refresh.
		if (!this.authService.isLoggedIn()) return;
		//TODO: If not logged in, wait until a login is complete.
		
		this.updatingContacts = true;
		this.userService.getContacts()
			.pipe(
				takeUntil(this._destroying$),
				catchError(err => {
					this.updatingContacts = false;
					this.dataError$.next();
					return EMPTY;
				}),
				tap(result => {
					if(!result || result.length === 0) {
						this.dataError$.next();
					}
				}),
				finalize(() => this.updatingContacts = false)
			)
			.subscribe(value => {
				this.userCache.setContacts(value);
			});
	}
}